
import { CSSProperties, ReactNode, useState, useMemo } from "react";
import { Link, useLoaderData, useNavigate, useSearchParams } from "react-router-dom"
//-------------------------------------------------
import { laborCategorys, laborShopsCategorys } from "src/components/works/types";  // *
import {
    payRollRuleChangeDate, boolAndReducer, 
    onlyUnique, numberReducer, round, emailsListFor1245Exception5x8,
    getDatefromWeekNum, objAscSort, weeknum,
    MyDate, simpleStatusColorCode, arrayReducer, objectReducer, weekIndexes,
} from "src/components/common/functions";
import Export from "./export";
//-------------------------------------------------
export type PayrollAssignment = {
    id: number | string;
    projectId?: string;
    projectNumber: string;
    organizationName: string;
    clientProjectNumber: string;
    AssignmentStatus: "Created" | "Submitted" | "Approved" | "Rejected";
    jobTagStatus?: string | null;
    billed: "Yes" | "No";
    userId: string;
    userEmail?: string;
    userName?: string;
    department?: string;
    region?: string;
    unionName: string;
    role?: string;
    laborCategory: string;
    truckNumber?: string;
    coords?: string[] | false;
    preDriver: "Yes" | "No";
    postDriver: "Yes" | "No";
    hadLunch: "Yes" | "No";
    travelStartTime: string;
    workStartTime: string;
    workEndTime: string;
    travelEndTime: string;
    startTime: string;
    endTime: string;
    projectName?: string;
};

type RawPayrollAssignment = Omit<PayrollAssignment, "coords"> & {
    coords?: string | string[] | false;
};

export type PayrollDay = {
    jobs: PayrollAssignment[];
    regular: number;
    ot1: number;
    ot2: number;
    travel: number;
};

export type PayrollUserDays = {
    hours: PayrollDay[];
    userId: string;
    userEmail?: string;
    userName?: string;
    department?: string;
    region?: string;
    unionName?: string;
    role?: string;
};

type PayrollLoaderData = {
    assignments: RawPayrollAssignment[];
    weeks: string[];
    week: string;
    activeDate: string;
    view: "month" | "week" | "day";
};

type PayrollView = "month" | "week" | "day";

type PayrollProps = {
    tabTitle: string;
};

export const gatherHours = (assignment: PayrollAssignment, unionName = assignment.unionName): number[] => {
    if(assignment.AssignmentStatus === "Created"){
        return [0, 0, 0]
    }
    const travelStart =  Date.parse(assignment.travelStartTime.replace(/-/g, "/"));
    const workStart = Date.parse(assignment.workStartTime.replace(/-/g, "/"));
    const workEnd = Date.parse(assignment.workEndTime.replace(/-/g, "/"));
    const travelEnd = Date.parse(assignment.travelEndTime.replace(/-/g, "/"));

    const isPreDriver = assignment.preDriver === "Yes"?true:false;
    const isPostDriver = assignment.postDriver === "Yes"?true:false;
    const driverDependentUnions = [
        "SC Local 300", 
        "SC Local 652", 
        "SC Local 1184", 
        "185",
        "304", 
        "270",
        "Local 294", 
        "Local 783", 
    ];
    const travel1 = !isPreDriver && driverDependentUnions.includes(unionName)?0:(workStart - travelStart) / 3600000;
    const travel2 = !isPostDriver && driverDependentUnions.includes(unionName)?0:(travelEnd - workEnd) / 3600000;
    const workHours = assignment.hadLunch === "Yes" ? (workEnd - workStart) / 3600000 - 0.5:(workEnd - workStart) / 3600000;
    return [workHours, travel1, travel2];
}
export const calculateHours = (
    workHours: number, 
    travel1: number, 
    travel2: number, 
    travelStart: number, 
    travelEnd: number, 
    unionName: string, 
    email = "", 
    isTravelTag: boolean,
    department = ""
): number[] => {
    if(workHours === 0 && travel1 === 0 && travel2 === 0){
        return [0, 0, 0, 0, 0]
    }
    const travel = travel1 + travel2;
    let regular = 0, ot1 = 0, ot2 = 0, travelHours = 0, abnormalHours = 0; // plus $3/h for regular
    switch(unionName){
        //-------------------------------------------------------------
        case "1245":
            //rate is same for everyBody
            if(travel1 > 1){
                workHours += travel1 - 1
            }
            if(travel2 > 1){
                workHours += travel2 - 1
            }
            //------------------------------------------------------
            if(!(emailsListFor1245Exception5x8 as string[]).includes(email)){
                if(workHours <= 4){
                    regular = 4;
                }else if(workHours <= 6){
                    regular = 6;
                }else if(workHours <= 8){
                    regular = 8;
                }else if(workHours <= 10){
                    regular = 10;
                }else{
                    regular = 10;
                    ot2 = workHours - 10;
                }
                //Friday: makeup day or OT
                if(
                    (new Date(travelStart)).getDay() === 5 ||
                    (new Date(travelStart)).getDay() === 6 ||
                    (new Date(travelStart)).getDay() === 0
               ){
                    regular = 0;
                    ot2 = workHours;
                }
            }else{
                if(workHours <= 4){
                    regular = 4;
                }else if(workHours <= 6){
                    regular = 6;
                }else if(workHours <= 8){
                    regular = 8;
                }else{
                    regular = 8;
                    ot2 = workHours - 8;
                }
                if(
                    (new Date(travelStart)).getDay() === 6 ||
                    (new Date(travelStart)).getDay() === 0
               ){
                    regular = 0;
                    ot2 = workHours;
                }
            }
            break;
        //-------------------------------------------------------------
        case "SC Local 300":
        case "SC Local 652":
        case "SC Local 1184":
        case "Local 783":
            if(workHours === 0 && isTravelTag){
                regular = 0;
            }else if(workHours <= 4){
                regular = 4;
            }else if(workHours <= 6){
                regular = 6;
            }else if(workHours <= 8){
                regular = 8;
            }else if(workHours <= 12){
                regular = 8;
                ot1 = workHours - 8;
            }else{
                regular = 8;
                ot1 = 4;
                ot2 = workHours - 12;
            }
            //Saturday ot1, Sunday ot2
            if((new Date(travelStart)).getDay() === 6){
                regular = 0;
                ot1 = workHours;
                ot2 = 0;
            }else if((new Date(travelStart)).getDay() === 0){
                regular = 0;
                ot1 = 0;
                ot2 = workHours;
            }
            // change here
            travelHours = travel;
            break;
        //-------------------------------------------------------------
        case "185":
        case "270":
        case "Local 294":
        case "304":
            if(department === "Locating" || department === "Outside Service" || department === "Scanning&Coring"){
                travelHours = (travel1 > 1? travel1 - 1: 0) + (travel2 > 1? travel2 - 1: 0);
            }else{
                travelHours = travel;
            }
            if(payRollRuleChangeDate(new Date(travelStart))){
                //summer: (4, 8)
                if(workHours <= 4){
                    regular = 4;
                }else if(workHours <= 8){
                    regular = 8;
                }else if(workHours <= 12){
                    regular = 8;
                    ot1 = workHours - 8;
                }else{
                    regular = 8;
                    ot1 = 4;
                    ot2 = workHours - 12;
                }
            }else{
                //winter: (4, 6, 8)
                if(workHours <= 4){
                    regular = 4;
                }else if(workHours <= 6){
                    regular = 6;
                }else if(workHours <= 8){
                    regular = 8;
                }else if(workHours <= 12){
                    regular = 8;
                    ot1 = workHours - 8;
                }else{
                    regular = 8;
                    ot1 = 4;
                    ot2 = workHours - 12;
                }
            }
            //Saturday ot1, Sunday ot2
            if((new Date(travelStart)).getDay() === 6){
                regular = 0;
                ot1 = workHours;
                ot2 = 0;
            }else if((new Date(travelStart)).getDay() === 0){
                regular = 0;
                ot1 = 0;
                ot2 = workHours;
            }
            //-------------------------------------------------------------
            //abnoral hours
            // const workStartT = timeStringToFloat(formData.get("workStartTime"));
            // const workEndT = timeStringToFloat(formData.get("workEndTime"));
            // if(workEndT > 16){
            //     if(workStartT < 16){
            //         abnormalHours = workEndT - 16;
            //     }else{
            //         abnormalHours = workEndT - workStartT;
            //     }
            // }
            break;
        //-------------------------------------------------------------
        case "Local 3":
            workHours += travel;
            const md = new MyDate(travelStart).s().slice(5, 10).replace(/-/g,"/");
            const in48Range = md >= "05/01" && md <= "10/31";
            if((new Date(travelStart)).getDay() === 6){
                //Saturday
                if(workHours === 0 && isTravelTag){
                    ot1 = 0;
                }else if(workHours <= 2 && !in48Range){
                    ot1 = 2;
                }else if(workHours <= 4){
                    ot1 = 4;
                }else if(workHours <= 6 && !in48Range){
                    ot1 = 6;
                }else if(workHours <= 8){
                    ot1 = 8;
                }else if(workHours <= 12){
                    ot1 = workHours;
                }else{
                    ot1 = 12;
                    ot2 = workHours - 12;
                }
            }else if((new Date(travelStart)).getDay() === 0){
                //Sunday
                if(workHours === 0 && isTravelTag){
                    ot2 = 0;
                }else if(workHours <= 2 && !in48Range){
                    ot2 = 2;
                }else if(workHours <= 4){
                    ot2 = 4;
                }else if(workHours <= 6 && !in48Range){
                    ot2 = 6;
                }else if(workHours <= 8){
                    ot2 = 8;
                }else{
                    ot2 = workHours;
                }
            }else if(
                (new Date(travelStart)).toLocaleDateString() !== 
                (new Date(travelEnd)).toLocaleDateString()
            ){
                // Overnight
                if(workHours === 0 && isTravelTag){
                    ot1 = 0;
                }else if(workHours <= 2 && !in48Range){
                    ot1 = 2;
                }else if(workHours <= 4){
                    ot1 = 4;
                }else if(workHours <= 6 && !in48Range){
                    ot1 = 6;
                }else if(workHours <= 8){
                    ot1 = 8;
                }else if(workHours <= 12){
                    ot1 = workHours;
                }else{
                    ot1 = 12;
                    ot2 = workHours - 12;
                }
            }else{
                if(workHours <= 2 && !in48Range){
                    regular = 2;
                }else if(workHours <= 4){
                    regular = 4;
                }else if(workHours <= 6 && !in48Range){
                    regular = 6;
                }else if(workHours <= 8){
                    regular = 8;
                }else if(workHours <= 12){
                    regular = 8;
                    ot1 = workHours - 8;
                }else{
                    regular = 8;
                    ot1 = 4;
                    ot2 = workHours - 12;
                }
            }
            //-------------------------------------------------------------
            //abnoral hours
            //const travelStartT = timeStringToFloat(formData.get("travelStartTime"));
            //const travelEndT = timeStringToFloat(formData.get("travelEndTime"));

            // if(travelStartT < 4 || travelStartT < 10){
            //     regular = 0;
            //     ot1 = workHours;
            //     ot2 = 0;
            // }

            //-------------------------------------------------------------
            // normal time between 10am to 4pm
            // if(travelStartT < 10){
            //     if(travelEndT > 10){
            //         abnormalHours += 10 - travelStartT;
            //     }else{
            //         abnormalHours = travelEndT - travelStartT;
            //     }
            // }
            // if(travelEndT > 16){
            //     if(travelStartT < 16){
            //         abnormalHours += travelEndT - 16;
            //     }else{
            //         abnormalHours = travelEndT - travelStartT;
            //     }
            // }
            //-------------------------------------------------------------
            break;
        //-------------------------------------------------------------
        case "Local 12":
            workHours += travel;
            if((new Date(travelStart)).getDay() === 6){
                //Saturday
                if(workHours === 0 && isTravelTag){
                    ot1 = 0;
                }else if(workHours <= 2){
                    ot1 = 2;
                }else if(workHours <= 4){
                    ot1 = 4;
                }else if(workHours <= 12){
                    ot1 = workHours;
                }else{
                    ot1 = 12;
                    ot2 = workHours - 12;
                }
            }else if((new Date(travelStart)).getDay() === 0){
                //Sunday
                if(workHours === 0 && isTravelTag){
                    ot2 = 0;
                }else if(workHours <= 2){
                    ot2 = 2;
                }else if(workHours <= 4){
                    ot2 = 4;
                }else{
                    ot2 = workHours;
                }
            }else if(
                (new Date(travelStart)).toLocaleDateString() !== 
                (new Date(travelEnd)).toLocaleDateString()
            ){
                //Overnight
                if(workHours === 0 && isTravelTag){
                    ot1 = 0;
                }else if(workHours <= 2){
                    ot1 = 2;
                }else if(workHours <= 4){
                    ot1 = 4;
                }else{
                    ot1 = 4;
                    ot2 = workHours - 4;
                }
            }else{
                if(workHours <= 2){
                    regular = 2;
                }else if(workHours <= 4){
                    regular = 4;
                }else if(workHours <= 8){
                    regular = workHours;
                }else if(workHours <= 12){
                    regular = 8;
                    ot1 = workHours - 8;
                }else{
                    regular = 8;
                    ot1 = 4;
                    ot2 = workHours - 12;
                }
            }
            //-------------------------------------------------------------
            //abnoral hours
            //const travelStartT = timeStringToFloat(formData.get("travelStartTime"));
            //const travelEndT = timeStringToFloat(formData.get("travelEndTime"));

            // if(travelStartT < 4 || travelStartT < 10){
            //     regular = 0;
            //     ot1 = workHours;
            //     ot2 = 0;
            // }

            //-------------------------------------------------------------
            // normal time between 10am to 4pm
            // if(travelStartT < 10){
            //     if(travelEndT > 10){
            //         abnormalHours += 10 - travelStartT;
            //     }else{
            //         abnormalHours = travelEndT - travelStartT;
            //     }
            // }
            // if(travelEndT > 16){
            //     if(travelStartT < 16){
            //         abnormalHours += travelEndT - 16;
            //     }else{
            //         abnormalHours = travelEndT - travelStartT;
            //     }
            // }
            //-------------------------------------------------------------
            break;
        //-------------------------------------------------------------
        default /*Non-Union*/:
            workHours += travel;
            if(workHours <= 8){
                regular = workHours;
            }else if(workHours <= 12){
                regular = 8;
                ot1 = workHours - 8;
            }else{
                regular = 8;
                ot1 = 4;
                ot2 = workHours - 12;
            }
            if((new Date(travelStart)).getDay() === 6){
                //Saturday
                regular = 0;
                ot1 = workHours;
                ot2 = 0;
            }else if((new Date(travelStart)).getDay() === 0){
                //Sunday
                regular = 0;
                ot1 = 0;
                ot2 = workHours;
            }
            break;
    }
    if(isTravelTag){
        regular = 0;
        ot1 = 0; 
        ot2 = 0;
        travelHours = travel;
        abnormalHours = 0;
    }
    return([regular, ot1, ot2, travelHours, abnormalHours]);
}
//-------------------------------------------------
export default function Payroll({ tabTitle }: PayrollProps){
    const { assignments, weeks, week, activeDate, view } = useLoaderData() as PayrollLoaderData;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const getTotalHours = (p: PayrollAssignment[][]): PayrollDay[] => {
        const outputArray: PayrollDay[] = [];
        p.forEach((f) => {
            const dataArray: number[][] = [];
            f.sort((a: PayrollAssignment, b: PayrollAssignment) => objAscSort(a, b, "travelStartTime")).forEach((ff: PayrollAssignment) => {
                dataArray.push(gatherHours(ff));
            });
            const dataArrayValid = dataArray.filter(d => d.length > 0);
            if(dataArrayValid.length){
                const totalHours = dataArrayValid.map(d => d.reduce(numberReducer, 0)).reduce(numberReducer, 0);
                const final = calculateHours(
                    totalHours - dataArrayValid.map(d => d[1]).reduce(numberReducer, 0) - dataArray.map(d => d[2]).reduce(numberReducer, 0),
                    dataArrayValid.map(d => d[1]).reduce(numberReducer, 0),
                    dataArrayValid.map(d => d[2]).reduce(numberReducer, 0),
                    Date.parse(f[0].travelStartTime.replace(/-/g, "/")),
                    Date.parse(f[0].travelEndTime.replace(/-/g, "/")),
                    f[0].unionName,
                    f[0].userEmail || "",
                    f[0].laborCategory === "Travel",
                    f[0].department
                );
                outputArray.push({
                    jobs: f.sort((a: PayrollAssignment, b: PayrollAssignment) => objAscSort(a, b, "travelStartTime")),
                    regular: round(final[0], 2),
                    ot1: round(final[1], 2),
                    ot2: round(final[2], 2),
                    travel: round(final[3], 2)
                });
            }else{
                outputArray.push({
                    jobs: f.sort((a: PayrollAssignment, b: PayrollAssignment) => objAscSort(a, b, "travelStartTime")),
                    regular: 0,
                    ot1: 0,
                    ot2: 0,
                    travel: 0
                });
            }
        });
        return outputArray;
    };

    const rawData = useMemo<PayrollAssignment[]>(() => (assignments || []).map((raw: RawPayrollAssignment) => ({...raw,
        travelStartTime: raw.travelStartTime?.replace(/-/g, "/"),
        workStartTime: raw.workStartTime?.replace(/-/g, "/"),
        workEndTime: raw.workEndTime?.replace(/-/g, "/"),
        travelEndTime: raw.travelEndTime?.replace(/-/g, "/"),
        startTime: raw.startTime?.replace(/-/g, "/"),
        endTime: raw.endTime?.replace(/-/g, "/"),
        coords: typeof raw.coords === "string" ? raw.coords.split(",") : raw.coords || false
    })), [assignments]);
    const [department, setDepartment] = useState("All");
    const [laborCategory, setLaborCategory] = useState("All");
    const [showHours, setShowHours] = useState(false);
    const [showTimes, setShowTimes] = useState(false);
    const [showOvertimes, setShowOvertimes] = useState(false);
    const [status, setStatus] = useState("All");
    const [filterWords, setFilterWords] = useState("");

    const departments = ["All",
        "Potholing",
        "Locating",
        "Scanning&Coring",
        "CCTV",
        "Mapping&Surveying",
        "QA/QC",
        "Traffic Control",
        "Hydro Vac Excavation",
        "Straight Vacuum Services",
        "LA",
        "Arizona"
    ];

    const filteredData = useMemo<PayrollAssignment[]>(() => rawData
        .map((raw: PayrollAssignment) => ({
            ...raw,
            projectName: `${raw.projectNumber} - ${raw.organizationName} - ${raw.clientProjectNumber}`,
            userName: raw.userName?.replace(/ {2}/g, " ")
        }))
        .filter((raw: PayrollAssignment) =>
            ((status !== "All"
                ? (status === "Billed" ? raw.billed === "Yes" : raw.AssignmentStatus === status)
                : true
            ) &&
            (department !== "All" ? raw.department === department : true) &&
            (laborCategory !== "All" ? raw.laborCategory === laborCategory : true) &&
            filterWords.toLowerCase().split(" ").map(f => JSON.stringify(
                [raw.userName, raw.projectName]
            ).toLowerCase().indexOf(f) > -1).reduce(boolAndReducer, true))
        ), [filterWords, rawData, status, department, laborCategory]);

    const userEveryDayCalculatedData = useMemo<PayrollUserDays[]>(() => {
        return filteredData
            .map(raw => raw.userId)
            .filter(onlyUnique)
            .map((userId: string) => {
                const userJobs = filteredData.filter(job => job.userId === userId);
                const jobsByDay = weekIndexes.map((_, i) =>
                    userJobs.filter(job => (new Date(job.startTime.substring(0, 10).replace(/-/g, "/"))).getDay() === i)
                );
                const jobUser = jobsByDay.reduce(arrayReducer, []).reduce(objectReducer, {}) as Partial<PayrollAssignment>;
                return {
                    hours: getTotalHours(jobsByDay),
                    userId: jobUser.userId || userId,
                    userEmail: jobUser.userEmail,
                    userName: jobUser.userName,
                    department: jobUser.department,
                    region: jobUser.region,
                    unionName: jobUser.unionName,
                    role: jobUser.role,
                };
            })
            .filter(user => !showOvertimes ? true : user.hours.map(day => day.ot1 + day.ot2).reduce(numberReducer, 0))
            .map(user => ({...user, hours: user.hours.map(day => ({
                ...day,
                jobs: showOvertimes && !(day.ot1 + day.ot2) ? [] : day.jobs,
                regular: showOvertimes && !(day.ot1 + day.ot2) ? 0 : day.regular,
                travel: showOvertimes && !(day.ot1 + day.ot2) ? 0 : day.travel,
            }))}));
    }, [filteredData, showOvertimes]);

    const activeDateObject = useMemo(() => new MyDate(activeDate.replace(/-/g, "/")), [activeDate]);
    const weekDates = useMemo(() => Array.from({ length: 7 }, (_, index) => getDatefromWeekNum(week, index) ?? ""), [week]);
    const visibleDates = useMemo(() => (view === "day" ? [activeDate] : view === "week" ? weekDates : []), [activeDate, view, weekDates]);
    const monthGridDates = useMemo(() => buildMonthGrid(activeDateObject), [activeDateObject]);
    const payrollEvents = useMemo<PayrollViewAssignment[]>(() => filteredData.map(assignment => {
        const hours = gatherHours(assignment);
        const startDate = new Date(assignment.startTime);
        const parsedEndDate = new Date(assignment.endTime);
        const endDate = parsedEndDate.getTime() > startDate.getTime()
            ? parsedEndDate
            : new Date(startDate.getTime() + 60 * 60 * 1000);
        return {
            ...assignment,
            displayStart: formatClockTime(startDate),
            displayEnd: formatClockTime(endDate),
            workHours: round(hours[0] ?? 0, 2),
            travelHours: round((hours[1] ?? 0) + (hours[2] ?? 0), 2),
            startDate,
            endDate,
        };
    }), [filteredData]);

    const summary = {
        created: payrollEvents.filter(value => value.AssignmentStatus === "Created").length,
        submitted: payrollEvents.filter(value => value.AssignmentStatus === "Submitted").length,
        approved: payrollEvents.filter(value => value.AssignmentStatus === "Approved").length,
        billed: payrollEvents.filter(value => value.billed === "Yes").length,
        rejected: payrollEvents.filter(value => value.AssignmentStatus === "Rejected").length,
    };

    const allDaySegments = useMemo(() => {
        const result: Record<string, PayrollViewAssignment[]> = {};
        visibleDates.forEach(date => {
            result[date] = payrollEvents.filter(assignment => isAllDayPayroll(assignment) && intersectsPayrollDate(assignment, date));
        });
        return result;
    }, [payrollEvents, visibleDates]);

    const timedSegments = useMemo(() => {
        const result: Record<string, PayrollTimedSegment[]> = {};
        visibleDates.forEach(date => {
            result[date] = payrollEvents
                .filter(assignment => !isAllDayPayroll(assignment))
                .map(assignment => getPayrollTimedSegment(assignment, date))
                .filter((segment): segment is PayrollTimedSegment => Boolean(segment))
                .sort((a, b) => a.top - b.top || b.height - a.height);
        });
        return result;
    }, [payrollEvents, visibleDates]);

    const monthAssignmentsByDate = useMemo(() => {
        const result: Record<string, PayrollViewAssignment[]> = {};
        monthGridDates.forEach(date => {
            result[date] = payrollEvents
                .filter(assignment => intersectsPayrollDate(assignment, date))
                .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        });
        return result;
    }, [monthGridDates, payrollEvents]);

    const navigateToState = (nextDate: string, nextView: PayrollView = view) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("date", nextDate);
        nextParams.set("view", nextView);
        navigate(`/Payroll/${weeknum(nextDate)}?${nextParams.toString()}`);
    };

    const navigateByOffset = (offset: number) => {
        const nextDate = shiftDate(activeDate, view === "month" ? { months: offset } : { days: view === "week" ? offset * 7 : offset });
        navigateToState(nextDate);
    };

    return (
        <div className = "calendarPage">
            <section className = "calendarToolbar">
                <div className = "calendarToolbarGrid">
                    <Field label = "Search" htmlFor = "payrollSearch">
                        <input
                            id = "payrollSearch"
                            className = "form-control"
                            placeholder = "Name or project"
                            type = "text"
                            value = {filterWords}
                            onChange = {e => setFilterWords(e.currentTarget.value.toLowerCase())}
                        />
                    </Field>
                    <Field label = "Status" htmlFor = "status">
                        <select id = "status" className = "form-control" value = {status} onChange = {e => setStatus(e.currentTarget.value)}>
                            <option>All</option>
                            <option value = "Created">Unsubmitted</option>
                            <option>Submitted</option>
                            <option>Approved</option>
                            <option>Billed</option>
                            <option>Rejected</option>
                        </select>
                    </Field>
                    <Field label = "Department" htmlFor = "department">
                        <select id = "department" className = "form-control" value = {department} onChange = {e => setDepartment(e.currentTarget.value)}>
                            {departments.map((d, key) => <option key = {key}>{d}</option>)}
                            <option>Outside Service</option>
                        </select>
                    </Field>
                    <Field label = "Labor Category" htmlFor = "laborCategory">
                        <select id = "laborCategory" className = "form-control" value = {laborCategory} onChange = {e => setLaborCategory(e.currentTarget.value)}>
                            <option>All</option>
                            <optgroup label = "Field Work">
                                {laborCategorys.map((d, key) => <option key = {key}>{d}</option>)}
                            </optgroup>
                            <optgroup label = "Shop Works">
                                {laborShopsCategorys.map((d, key) => <option key = {key}>{d}</option>)}
                            </optgroup>
                        </select>
                    </Field>
                </div>

                <div className = "calendarToolbarFooter">
                    <ToggleField label = "Show Times" htmlFor = "showTime">
                        <input id = "showTime" type = "checkbox" checked = {showTimes} className = "cssCheckBox" onChange = {e => setShowTimes(e.currentTarget.checked)} />
                    </ToggleField>
                    <ToggleField label = "Show Hours" htmlFor = "showHours">
                        <input id = "showHours" type = "checkbox" checked = {showHours} className = "cssCheckBox" onChange = {e => setShowHours(e.currentTarget.checked)} />
                    </ToggleField>
                    <ToggleField label = "Only Overtimes" htmlFor = "showOvertimes">
                        <input id = "showOvertimes" type = "checkbox" checked = {showOvertimes} className = "cssCheckBox" onChange = {e => setShowOvertimes(e.currentTarget.checked)} />
                    </ToggleField>
                    <div className = "payrollExportWrap">
                        <Export userEveryDayCalculatedData = {userEveryDayCalculatedData} filteredData = {filteredData} />
                    </div>
                </div>
            </section>
            <section className = "calendarLegend">
                {payrollStatusLegend.map(item => (
                    <div className = {`calendarLegendChip ${item.className}`} key = {item.key}>
                        <span>{item.label}</span>
                        <strong>{summary[item.key]}</strong>
                    </div>
                ))}
            </section>
            <div className = "calendarNavInputs">
                <div className = "calendarViewSwitch" role = "tablist" aria-label = "Payroll views">
                    {(["month", "week", "day"] as PayrollView[]).map(option => (
                        <button
                            key = {option}
                            className = {`calendarViewButton${view === option ? " active" : ""}`}
                            type = "button"
                            onClick = {() => navigateToState(activeDate, option)}
                        >
                            {capitalize(option)}
                        </button>
                    ))}
                </div>
                {view === "month" ? (
                    <Field htmlFor = "payrollMonth">
                        <select
                            id = "payrollMonth"
                            className = "form-control"
                            value = {activeDate.slice(0, 7)}
                            onChange = {e => navigateToState(`${e.currentTarget.value}-01`)}
                        >
                            {getMonthOptions(weeks).map((value, index) => (
                                <option key = {index} value = {value}>{formatMonthOption(value)}</option>
                            ))}
                        </select>
                    </Field>
                ) : null}
                {view === "week" ? (
                    <Field htmlFor = "payrollWeek">
                        <select
                            id = "payrollWeek"
                            className = "form-control"
                            value = {week}
                            onChange = {e => navigateToState(getDatefromWeekNum(e.currentTarget.value, 0) ?? activeDate)}
                        >
                            {weeks.map((wk, key) => <option key = {key}>{wk}</option>)}
                        </select>
                    </Field>
                ) : null}
                {view === "day" ? (
                    <Field htmlFor = "payrollDate">
                        <input
                            id = "payrollDate"
                            className = "form-control"
                            type = "date"
                            value = {activeDate}
                            onChange = {e => navigateToState(e.currentTarget.value)}
                        />
                    </Field>
                ) : null}
                <div className = "calendarNavButtons">
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(-1)}>Prev</button>
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(1)}>Next</button>
                </div>
            </div>
            {view === "month" ? (
                <PayrollMonthView
                    activeDate = {activeDate}
                    monthGridDates = {monthGridDates}
                    assignmentsByDate = {monthAssignmentsByDate}
                    onSelectDate = {date => navigateToState(date, "day")}
                    showTimes = {showTimes}
                    showHours = {showHours}
                />
            ) : (
                <PayrollTimedView
                    view = {view}
                    visibleDates = {visibleDates}
                    allDaySegments = {allDaySegments}
                    timedSegments = {timedSegments}
                    showTimes = {showTimes}
                    showHours = {showHours}
                    onSelectDate = {date => navigateToState(date, "day")}
                />
            )}
        </div>
    );
}

type PayrollViewAssignment = PayrollAssignment & {
    displayStart: string;
    displayEnd: string;
    workHours: number;
    travelHours: number;
    startDate: Date;
    endDate: Date;
};

type PayrollTimedSegment = {
    assignment: PayrollViewAssignment;
    top: number;
    height: number;
    startsBefore: boolean;
    endsAfter: boolean;
};

const payrollHourHeight = 64;
const payrollMinHeight = 28;
const payrollDayNamesShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const payrollDayNamesLong = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const payrollStatusLegend = [
    { key: "created", label: "Unsubmitted", className: "calendarLegendChipUnsubmitted" },
    { key: "submitted", label: "Submitted", className: "calendarLegendChipSubmitted" },
    { key: "approved", label: "Approved", className: "calendarLegendChipApproved" },
    { key: "billed", label: "Billed", className: "calendarLegendChipBilled" },
    { key: "rejected", label: "Rejected", className: "calendarLegendChipRejected" },
] as const;
const payrollHourLabels = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: formatHourLabel(hour),
}));

function PayrollTimedView({
    view,
    visibleDates,
    allDaySegments,
    timedSegments,
    showTimes,
    showHours,
    onSelectDate,
}: {
    view: "week" | "day";
    visibleDates: string[];
    allDaySegments: Record<string, PayrollViewAssignment[]>;
    timedSegments: Record<string, PayrollTimedSegment[]>;
    showTimes: boolean;
    showHours: boolean;
    onSelectDate: (date: string) => void;
}) {
    const gridHeight = payrollHourHeight * 24;
    const hasTimedAssignments = visibleDates.some(date => timedSegments[date]?.length || allDaySegments[date]?.length);

    return (
        <section className = "calendarTimedShell">
            <div className = "calendarAllDayRow">
                <div className = "calendarTimeAxisSpacer">All day</div>
                <div className = {`calendarAllDayColumns cols-${visibleDates.length}`}>
                    {visibleDates.map((date, index) => (
                        <div className = "calendarAllDayColumn" key = {date}>
                            <button className = "calendarTimedDayHeader" type = "button" onClick = {() => onSelectDate(date)}>
                                <span>{view === "week" ? payrollDayNamesShort[index] : payrollDayNamesLong[(new Date(date.replace(/-/g, "/"))).getDay() === 0 ? 6 : (new Date(date.replace(/-/g, "/"))).getDay() - 1]}</span>
                                <strong>{formatShortDate(date)}</strong>
                            </button>
                            <div className = "calendarAllDayEvents">
                                {allDaySegments[date]?.length ? allDaySegments[date].map(item => (
                                    <Link className = "calendarAllDayEvent" key = {`${date}-${item.id}`} to = {`/Assignments/${item.id}`}>
                                        <span className = "calendarStatusDot" style = {{ backgroundColor: getPayrollStatusColor(item.AssignmentStatus, item.billed) }} />
                                        <span className = "calendarAllDayText">{item.userName}</span>
                                    </Link>
                                )) : <div className = "calendarNoAllDay">No all-day events</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className = "calendarTimedFrame">
                <div className = "calendarTimeAxis">
                    {payrollHourLabels.map(row => (
                        <div className = "calendarTimeAxisRow" key = {row.hour} style = {{ height: payrollHourHeight }}>
                            <span>{row.label}</span>
                        </div>
                    ))}
                </div>
                <div className = {`calendarTimedColumns cols-${visibleDates.length}`} style = {{ minHeight: gridHeight }}>
                    {visibleDates.map((date, index) => (
                        <div className = "calendarTimedColumn" key = {date}>
                            <div className = "calendarTimedColumnGrid">
                                {payrollHourLabels.map(row => (
                                    <div className = "calendarTimedHourSlot" key = {row.hour} style = {{ height: payrollHourHeight }} />
                                ))}
                            </div>
                            <div className = "calendarTimedEventsLayer">
                                {timedSegments[date]?.map(segment => (
                                    <PayrollTimedEventCard
                                        key = {`${date}-${segment.assignment.id}-${segment.top}`}
                                        segment = {segment}
                                        showTimes = {showTimes}
                                        showHours = {showHours}
                                    />
                                ))}
                                {!timedSegments[date]?.length && !allDaySegments[date]?.length ? (
                                    <div className = "calendarTimedEmpty">No assignments</div>
                                ) : null}
                            </div>
                            {view === "week" && index < visibleDates.length - 1 ? <div className = "calendarTimedColumnDivider" /> : null}
                        </div>
                    ))}
                    {!hasTimedAssignments ? <div className = "calendarTimedNoResults">No assignments match the current filters.</div> : null}
                </div>
            </div>
        </section>
    );
}

function PayrollMonthView({
    activeDate,
    monthGridDates,
    assignmentsByDate,
    onSelectDate,
    showTimes,
    showHours,
}: {
    activeDate: string;
    monthGridDates: string[];
    assignmentsByDate: Record<string, PayrollViewAssignment[]>;
    onSelectDate: (date: string) => void;
    showTimes: boolean;
    showHours: boolean;
}) {
    const activeMonth = activeDate.slice(0, 7);

    return (
        <section className = "calendarMonthView">
            <div className = "calendarMonthHeader">
                {payrollDayNamesShort.map(day => <div className = "calendarMonthHeaderCell" key = {day}>{day}</div>)}
            </div>
            <div className = "calendarMonthGrid">
                {monthGridDates.map(date => {
                    const items = assignmentsByDate[date] ?? [];
                    const inMonth = date.slice(0, 7) === activeMonth;
                    return (
                        <div className = {`calendarMonthCell${inMonth ? "" : " outside"}${date === activeDate ? " active" : ""}`} key = {date}>
                            <button className = "calendarMonthDateButton" type = "button" onClick = {() => onSelectDate(date)}>
                                <div className = "calendarMonthDateRow">
                                    <span>{date.slice(8, 10)}</span>
                                    <strong>{items.length || ""}</strong>
                                </div>
                            </button>
                            <div className = "calendarMonthAssignments">
                                {items.slice(0, 3).map(item => (
                                    <Link className = "calendarMonthAssignment calendarMonthAssignmentLink" to = {`/Assignments/${item.id}`} key = {`${date}-${item.id}`}>
                                        <span className = "calendarMonthAssignmentDot" style = {{ backgroundColor: getPayrollStatusColor(item.AssignmentStatus, item.billed) }} />
                                        <span className = "calendarMonthAssignmentText">
                                            {showTimes && !isAllDayPayroll(item) ? `${item.displayStart} ` : ""}
                                            {item.userName}
                                        </span>
                                        {showHours && item.AssignmentStatus !== "Created" ? (
                                            <span className = "calendarMonthAssignmentHours">{String(item.workHours)}h</span>
                                        ) : null}
                                    </Link>
                                ))}
                                {items.length > 3 ? <div className = "calendarMonthMore">+{items.length - 3} more</div> : null}
                                {!items.length ? <div className = "calendarMonthEmpty">No assignments</div> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function PayrollTimedEventCard({
    segment,
    showTimes,
    showHours,
}: {
    segment: PayrollTimedSegment;
    showTimes: boolean;
    showHours: boolean;
}) {
    const { assignment } = segment;
    const style: CSSProperties = {
        top: segment.top,
        height: segment.height,
        borderLeftColor: getPayrollStatusColor(assignment.AssignmentStatus, assignment.billed),
    };

    return (
        <Link className = "calendarTimedEvent" style = {style} to = {`/Assignments/${assignment.id}`}>
            <div className = "calendarTimedEventTitle">{assignment.userName}</div>
            <div className = "calendarTimedEventProject">{assignment.projectNumber}</div>
            <div className = "calendarTimedEventMeta">{assignment.projectName}</div>
            <div className = "calendarTimedEventMeta">{showTimes ? `${segment.startsBefore ? "..." : assignment.displayStart} - ${segment.endsAfter ? "..." : assignment.displayEnd}` : assignment.laborCategory}</div>
            <div className = "calendarTimedEventMeta">{assignment.laborCategory}{assignment.truckNumber ? ` • ${assignment.truckNumber}` : ""}</div>
            {showHours ? <div className = "calendarTimedEventMeta">Work {assignment.workHours}h • Travel {assignment.travelHours}h</div> : null}
        </Link>
    );
}

function Field({ label, htmlFor, children }: { label?: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarField" htmlFor = {htmlFor}>
            {label?<span className = "calendarFieldLabel">{label}</span>:null} 
            {children}
        </label>
    );
}

function ToggleField({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarToggle" htmlFor = {htmlFor}>
            <span className = "calendarToggleText">{label}</span>
            {children}
        </label>
    );
}

function buildMonthGrid(activeDate: Date) {
    const firstOfMonth = new MyDate(activeDate.getFullYear(), activeDate.getMonth(), 1);
    const firstDayIndex = getMondayOffset(firstOfMonth.s());
    const gridStart = new MyDate(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstDayIndex);
    return Array.from({ length: 42 }, (_, index) => {
        const date = new MyDate(gridStart);
        date.setDate(gridStart.getDate() + index);
        return date.s();
    });
}

function getMondayOffset(dateString: string) {
    const weekday = new Date(dateString.replace(/-/g, "/")).getDay();
    return weekday === 0 ? 6 : weekday - 1;
}

function shiftDate(dateString: string, offset: { days?: number; months?: number }) {
    const date = new MyDate(dateString.replace(/-/g, "/"));
    if (offset.months) date.setMonth(date.getMonth() + offset.months);
    if (offset.days) date.setDate(date.getDate() + offset.days);
    return date.s();
}

function getMonthOptions(weeks: string[]) {
    return Array.from(new Set(weeks
        .map(curWeek => getDatefromWeekNum(curWeek, 0)?.slice(0, 7))
        .filter((value): value is string => Boolean(value))
    ));
}

function formatMonthOption(value: string) {
    return new Date(`${value}-01`.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });
}

function formatShortDate(date: string) {
    return new Date(date.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

function formatHourLabel(hour: number) {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
}

function formatClockTime(date: Date) {
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const suffix = hours >= 12 ? "PM" : "AM";
    const normalizedHour = hours % 12 || 12;
    return `${normalizedHour}:${minutes} ${suffix}`;
}

function capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function isAllDayPayroll(item: PayrollViewAssignment) {
    return item.startTime.substring(11, 16) === "00:00" && item.endTime.substring(11, 16) === "00:00";
}

function intersectsPayrollDate(item: PayrollViewAssignment, date: string) {
    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    return item.endDate > dayStart && item.startDate < dayEnd;
}

function getPayrollTimedSegment(item: PayrollViewAssignment, date: string): PayrollTimedSegment | null {
    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    if (item.endDate <= dayStart || item.startDate >= dayEnd) return null;

    const segmentStart = item.startDate < dayStart ? dayStart : item.startDate;
    const segmentEnd = item.endDate > dayEnd ? dayEnd : item.endDate;
    const top = (((segmentStart.getHours() * 60) + segmentStart.getMinutes()) / 60) * payrollHourHeight;
    const rawHeight = ((segmentEnd.getTime() - segmentStart.getTime()) / 3600000) * payrollHourHeight;

    return {
        assignment: item,
        top,
        height: Math.max(payrollMinHeight, rawHeight),
        startsBefore: item.startDate < dayStart,
        endsAfter: item.endDate > dayEnd,
    };
}

function getPayrollStatusColor(status: PayrollAssignment["AssignmentStatus"], billed: PayrollAssignment["billed"]) {
    if (billed === "Yes") return "pink";
    return simpleStatusColorCode[status as keyof typeof simpleStatusColorCode] ?? "black";
}
